import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import { products } from '../data/mockData';
import { productSEO } from '../data/seoData';
import SEO from '../components/SEO';
import { productSchema, breadcrumb } from '../data/structuredData';
import ProductGallery from '../components/product-detail/ProductGallery';
import ProductQuickInfo from '../components/product-detail/ProductQuickInfo';
import ProductCollaborator from '../components/product-detail/ProductCollaborator';
import FeaturesApplications from '../components/product-detail/FeaturesApplications';
import RelatedProducts from '../components/product-detail/RelatedProducts';

const ProductDetail = () => {
  const { slug } = useParams();
  const product = products.find((p) => p.slug === slug);
  const [selectedImage, setSelectedImage] = useState(0);

  // Must stay above the early-return to satisfy rules-of-hooks.
  const relatedProducts = useMemo(
    () =>
      product
        ? products
            .filter((p) => p.category === product.category && p.id !== product.id)
            .slice(0, 3)
        : [],
    [product]
  );

  if (!product) {
    return (
      <div className="min-h-screen bg-[#f9fafb] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/products" className="text-cyan-600 hover:text-cyan-700 font-semibold">
            Return to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb] overflow-hidden">
      <SEO
        title={
          (productSEO[product.slug] && productSEO[product.slug].title) ||
          `${product.name} | ${product.category} | Stallion Bulktech`
        }
        description={
          (productSEO[product.slug] && productSEO[product.slug].description) ||
          `${product.shortDescription} Engineered and manufactured by Stallion Bulktech for industrial safety and reliability.`
        }
        path={`/products/${product.slug}`}
        image={(product.images && product.images[0]) || product.image}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            productSchema(product),
            breadcrumb([
              { name: 'Home', path: '/' },
              { name: 'Products', path: '/products' },
              { name: product.category, path: `/products?category=${encodeURIComponent(product.category)}` },
              { name: product.name, path: `/products/${product.slug}` },
            ]),
          ],
        }}
      />
      {/* Breadcrumb & Back */}
      <section className="pt-24 md:pt-32 pb-6 md:pb-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={`/products?category=${encodeURIComponent(product.category)}`}
            className="inline-flex items-center space-x-2 text-gray-600 hover:text-cyan-600 transition-colors mb-4 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to {product.category}</span>
          </Link>
          <div className="text-sm text-gray-500">
            <Link to="/" className="hover:text-cyan-600">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/products" className="hover:text-cyan-600">Products</Link>
            <span className="mx-2">/</span>
            <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-cyan-600">
              {product.category}
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700">{product.name}</span>
          </div>
        </div>
      </section>

      {/* Product Detail */}
      <section className="py-8 md:py-12 bg-gradient-to-b from-white to-[#f9fafb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
            <ProductGallery
              product={product}
              selectedImage={selectedImage}
              setSelectedImage={setSelectedImage}
            />

            {/* Product Info */}
            <div>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-4">
                <Shield className="h-4 w-4 text-cyan-600" />
                <p className="text-cyan-600 text-sm font-medium">{product.category}</p>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-5 md:mb-6">
                {product.name}
              </h1>
              <p className="text-base md:text-lg text-gray-600 mb-8 leading-relaxed">
                {product.shortDescription}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-10 md:mb-12">
                <Link to="/contact" className="group relative w-full sm:w-auto" data-testid="product-request-quote">
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl blur opacity-50 group-hover:opacity-100 transition-opacity" />
                  <div className="relative flex items-center justify-center space-x-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl px-8 py-4 min-h-[52px] transform group-hover:scale-105 transition-all shadow-lg">
                    <span>Request a Quote</span>
                  </div>
                </Link>
                <Link to="/contact" className="group relative w-full sm:w-auto" data-testid="product-contact-us">
                  <div className="relative flex items-center justify-center bg-white/90 backdrop-blur-xl border-2 border-gray-300 text-gray-700 rounded-xl px-8 py-4 min-h-[52px] hover:bg-white hover:border-cyan-500 hover:text-cyan-700 transform group-hover:scale-105 transition-all">
                    Contact Us
                  </div>
                </Link>
              </div>

              <ProductQuickInfo product={product} />
              <ProductCollaborator product={product} />
            </div>
          </div>
        </div>
      </section>

      <FeaturesApplications product={product} />
      <RelatedProducts relatedProducts={relatedProducts} />

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-600 bg-size-200 animate-gradient" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-6">
            Interested in {product.name}?
          </h2>
          <p className="text-base md:text-xl text-white/90 mb-8 md:mb-10">
            Contact us today to discuss your requirements and get a customized quote
          </p>
          <Link to="/contact" className="group relative inline-flex items-center space-x-2">
            <div className="absolute inset-0 bg-white rounded-xl blur opacity-50 group-hover:opacity-100 transition-opacity" />
            <div className="relative flex items-center space-x-2 bg-white text-cyan-600 px-10 py-5 rounded-xl font-bold text-lg transform group-hover:scale-105 transition-all shadow-2xl">
              <span>Get in Touch</span>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
